import React, { useState } from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Tramo, Pozo, Pad, Operadora, Equipo, Trepano } from '../../types';
import { tramoFormFields } from '../../config/formFields';

interface TramosSectionProps {
  data: {
    tramos: Tramo[];
    pozos: Pozo[];
    pads: Pad[];
    operadoras: Operadora[];
    equipos: Equipo[];
    trepanos: Trepano[]; // Added trepanos
  };
  operations: {
    handleTramoSubmit: (data: Record<string, any>) => Promise<void>;
    handleTramoDelete: (item: Tramo) => Promise<void>;
    handleTramoEdit: (item: Tramo, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const TramosSection: React.FC<TramosSectionProps> = ({ data, operations, userPermissions }) => {
  // Filter state for custom view: Operadora → Equipo → Pad → Pozo → Tramos
  const [selectedOperadoraId, setSelectedOperadoraId] = useState<string>('');
  const [selectedEquipoId, setSelectedEquipoId] = useState<string>('');
  const [selectedPadId, setSelectedPadId] = useState<string>('');
  const [selectedPozoId, setSelectedPozoId] = useState<string>('');
  const [newTramoData, setNewTramoData] = useState({
    fase: '',
    tamanioTrepan: '',
    profundidadObjetivo: ''
  });
  const [customFase, setCustomFase] = useState('');
  const [showCustomFaseInput, setShowCustomFaseInput] = useState(false);

  // Standard phase options with display name and abbreviated form
  const standardPhases = [
    { value: 'Guía', abbreviation: 'GUIA' },
    { value: 'Intermedia I', abbreviation: 'INT I' },
    { value: 'Intermedia II', abbreviation: 'INT II' },
    { value: 'Piloto', abbreviation: 'PIL' },
    { value: 'Aislación', abbreviation: 'AISL' }
  ];

  // Get display name for fase (abbreviated if standard, full if custom)
  const getFaseDisplayName = (fase: string): string => {
    const standard = standardPhases.find(p => p.value === fase);
    return standard ? standard.abbreviation : fase;
  };

  // Filter equipos based on operadora selection
  const filteredEquipos = selectedOperadoraId
    ? data.equipos.filter(eq => eq.habilitado &&
        data.pads.some(pad => pad.operadoraId === selectedOperadoraId && pad.equipoId === eq.id))
    :
 [];

  // Filter pads based on exact operadora+equipo combination
  const filteredPads = (selectedOperadoraId && selectedEquipoId)
    ? data.pads.filter(pad =>
        pad.operadoraId === selectedOperadoraId && pad.equipoId === selectedEquipoId)
    : [];

  // Filter pozos based on exact pad selection
  const filteredPozos = selectedPadId
    ? data.pozos.filter(pozo => pozo.padId === selectedPadId)
    : [];

  // Filter tramos based on exact pozo selection
  const filteredTramos = selectedPozoId
    ? data.tramos.filter(tramo => tramo.pozoId === selectedPozoId)
    : [];

  // Handle new tramo creation
  const handleAddTramo = async () => {
    if (!newTramoData.fase.trim() || !newTramoData.tamanioTrepan.trim() || !selectedPozoId) {
      alert('⚠️ Debes completar todos los campos para agregar un tramo');
      return;
    }

    try {
      await operations.handleTramoSubmit({
        fase: newTramoData.fase.trim(),
        tamanioTrepan: newTramoData.tamanioTrepan.trim(),
        profundidadObjetivo: parseFloat(newTramoData.profundidadObjetivo) || 0,
        pozoId: selectedPozoId
      });
      setNewTramoData({ fase: '', tamanioTrepan: '', profundidadObjetivo: '' });
      setShowCustomFaseInput(false);
      setCustomFase('');
      alert('✅ Tramo agregado exitosamente');
    } catch (error) {
      console.error('Error adding tramo:', error);
      alert('❌ Error al agregar tramo');
    }
  };

  // Table column configurations - using actual Tramo fields including pozoId
  const tramoColumns: TableColumn<Tramo>[] = [
    {
      key: 'fase',
      title: 'Fase del Tramo',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleTramoEdit
    },
    {
      key: 'tamanioTrepan',
      title: 'Tamaño Trepano',
      width: '15%',
      editable: true,
      editType: 'select',
      selectOptions: data.trepanos.map(t => ({ value: t.nombre, label: t.nombre })),
      onEdit: operations.handleTramoEdit
    },
    {
      key: 'profundidadObjetivo',
      title: 'Prof. Objetivo (m)',
      width: '15%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleTramoEdit,
      render: (value) => value ? `${value} m` : '0 m'
    },
    {
      key: 'pozoId',
      title: 'Pozo',
      width: '35%',
      editable: true,
      editType: 'select',
      selectOptions: data.pozos.map(pozo => {
        const pad = data.pads.find(p => p.id === pozo.padId);
        const operadora = pad ? data.operadoras.find(op => op.id === pad.operadoraId) : null;
        return { value: pozo.id, label: `${pozo.nombre} (${operadora?.nombre || 'Sin Op'})` };
      }),
      onEdit: operations.handleTramoEdit,
      render: (value) => {
        const pozo = data.pozos.find(p => p.id === value);
        if (pozo) {
          const pad = data.pads.find(p => p.id === pozo.padId);
          const operadora = pad ? data.operadoras.find(op => op.id === pad.operadoraId) : null;
          return `${pozo.nombre} (${operadora?.nombre || 'Sin Op'})`;
        }
        return '(Sin pozo)';
      }
    }
  ];

  // Quick Setup Handler for standard tramos - using actual field names
  const handleTramosQuickSetup = async () => {
    if (window.confirm('¿Cargar tramos estándar? Esta acción creará tramos de ejemplo con fases estándar.')) {
      const tramoTemplates = [
        { fase: 'Conductor', tamanioTrepan: '26"', profundidadObjetivo: 100 },
        { fase: 'Superficie', tamanioTrepan: '17 1/2"', profundidadObjetivo: 500 },
        { fase: 'Intermedio I', tamanioTrepan: '12 1/4"', profundidadObjetivo: 1200 },
        { fase: 'Intermedio II', tamanioTrepan: '8 1/2"', profundidadObjetivo: 2000 },
        { fase: 'Producción', tamanioTrepan: '6 1/8"', profundidadObjetivo: 2800 }
      ];

      try {
        for (const template of tramoTemplates) {
          await operations.handleTramoSubmit(template);
        }
        alert('✅ Tramos estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando tramos:', error);
        alert('❌ Error cargando tramos. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>📏 Gestión de Tramos</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra las fases de perforación y especificaciones de trepanos
        </p>
      </div>

      {/* Custom Filtered Tramos Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#333', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📏 Tramos de Perforación
        </h3>

        {/* Cascading Filter Controls */}
        <div style={{
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {/* Step 1: Operadora Selector (Always visible) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>1. Seleccionar Operadora:</label>
            <select
              value={selectedOperadoraId}
              onChange={(e) => {
                setSelectedOperadoraId(e.target.value);
                setSelectedEquipoId('');
                setSelectedPadId('');
                setSelectedPozoId('');
                setNewTramoData({ fase: '', tamanioTrepan: '', profundidadObjetivo: '' });
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '160px'
              }}
            >
              <option value="">Seleccionar operadora...</option>
              {data.operadoras.filter(op => op.habilitada).map(operadora => (
                <option key={operadora.id} value={operadora.id}>
                  {operadora.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Equipo Selector */}
          {selectedOperadoraId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>2. Seleccionar Equipo:</label>
              <select
                value={selectedEquipoId}
                onChange={(e) => {
                  setSelectedEquipoId(e.target.value);
                  setSelectedPadId('');
                  setSelectedPozoId('');
                  setNewTramoData({ fase: '', tamanioTrepan: '', profundidadObjetivo: '' });
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '160px'
                }}
              >
                <option value="">Seleccionar equipo...</option>
                {filteredEquipos.map(equipo => (
                  <option key={equipo.id} value={equipo.id}>
                    {equipo.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3: Pad Selector */}
          {selectedOperadoraId && selectedEquipoId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>3. Seleccionar Pad:</label>
              <select
                value={selectedPadId}
                onChange={(e) => {
                  setSelectedPadId(e.target.value);
                  setSelectedPozoId('');
                  setNewTramoData({ fase: '', tamanioTrepan: '', profundidadObjetivo: '' });
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '160px'
                }}
              >
                <option value="">Seleccionar pad...</option>
                {filteredPads.map(pad => (
                  <option key={pad.id} value={pad.id}>
                    {pad.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 4: Pozo Selector */}
          {selectedPadId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>4. Seleccionar Pozo:</label>
              <select
                value={selectedPozoId}
                onChange={(e) => {
                  setSelectedPozoId(e.target.value);
                  setNewTramoData({ fase: '', tamanioTrepan: '', profundidadObjetivo: '' });
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '160px'
                }}
              >
                <option value="">Seleccionar pozo...</option>
                {filteredPozos.map(pozo => (
                  <option key={pozo.id} value={pozo.id}>
                    {pozo.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Step 5: Add New Tramo Fields (Only appears when pozo is selected) */}
        {selectedPozoId && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            marginBottom: '20px'
          }}>
            <h4 style={{ color: '#333', marginBottom: '15px' }}>5. Agregar Nuevo Tramo:</h4>
            <div style={{
              display: 'flex',
              gap: '15px',
              alignItems: 'end',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Fase:</label>
                <select
                  value={showCustomFaseInput ? 'Otro' : newTramoData.fase}
                  onChange={(e) => {
                    if (e.target.value === 'Otro') {
                      setShowCustomFaseInput(true);
                      setNewTramoData(prev => ({ ...prev, fase: '' }));
                      setCustomFase('');
                    } else {
                      setShowCustomFaseInput(false);
                      setNewTramoData(prev => ({ ...prev, fase: e.target.value }));
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                >
                  <option value="">Seleccionar fase...</option>
                  {standardPhases.map(phase => (
                    <option key={phase.value} value={phase.value}>
                      {phase.value}
                    </option>
                  ))}
                  <option value="Otro">Otro...</option>
                </select>
              </div>

              {/* Custom Fase Input (only shown when "Otro" is selected) */}
              {showCustomFaseInput && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Nombre de Fase:</label>
                  <input
                    type="text"
                    value={customFase}
                    onChange={(e) => {
                      setCustomFase(e.target.value);
                      setNewTramoData(prev => ({ ...prev, fase: e.target.value }));
                    }}
                    placeholder="Escribir fase personalizada"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Tamaño Trepano:</label>
                <select
                  value={newTramoData.tamanioTrepan}
                  onChange={(e) => setNewTramoData(prev => ({ ...prev, tamanioTrepan: e.target.value }))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                >
                  <option value="">Seleccionar tamaño...</option>
                  {data.trepanos.map(trepano => (
                    <option key={trepano.id} value={trepano.nombre}>
                      {trepano.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Prof. Objetivo (m):</label>
                <input
                  type="number"
                  value={newTramoData.profundidadObjetivo}
                  onChange={(e) => setNewTramoData(prev => ({ ...prev, profundidadObjetivo: e.target.value }))}
                  placeholder="0"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '120px'
                  }}
                />
              </div>

              <button
                onClick={handleAddTramo}
                disabled={!newTramoData.fase.trim() || !newTramoData.tamanioTrepan.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (newTramoData.fase.trim() && newTramoData.tamanioTrepan.trim()) ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (newTramoData.fase.trim() && newTramoData.tamanioTrepan.trim()) ? 'pointer' : 'not-allowed',
                  fontSize: '14px'
                }}
              >
                ➕ Agregar Tramo
              </button>
            </div>
          </div>
        )}

        {/* Filtered Tramos List (Only appears when pozo is selected) */}
        {selectedPozoId && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '1px solid #eee'
            }}>
              <h4 style={{ color: '#333', margin: 0 }}>
                Tramos en {data.pozos.find(p => p.id === selectedPozoId)?.nombre} ({data.operadoras.find(op => op.id === selectedOperadoraId)?.nombre} - {data.equipos.find(eq => eq.id === selectedEquipoId)?.nombre} - {data.pads.find(p => p.id === selectedPadId)?.nombre})
              </h4>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {filteredTramos.length} tramo{filteredTramos.length !== 1 ? 's' : ''}
              </span>
            </div>

            {filteredTramos.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '10px'
              }}>
                {filteredTramos.map(tramo => (
                  <div key={tramo.id} style={{
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    transition: 'box-shadow 0.2s'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '10px',
                      color: '#333',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>📏 {getFaseDisplayName(tramo.fase)}</span>
                      <button
                        onClick={() => operations.handleTramoDelete(tramo)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Eliminar tramo"
                      >
                        🗑️
                      </button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      🔧 Trepano: {tramo.tamanioTrepan}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      📊 Prof. Objetivo: {tramo.profundidadObjetivo} m
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      🕳️ Pozo: {data.pozos.find(p => p.id === tramo.pozoId)?.nombre}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #ddd'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📏</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  No hay tramos en este pozo
                </div>
                <div style={{ fontSize: '14px' }}>
                  Agrega el primer tramo usando los campos de arriba
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Setup Button */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={handleTramosQuickSetup}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📏 Cargar Tramos Estándar
          </button>
        </div>
      </div>

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e3f2fd',
        borderRadius: '12px',
        border: '2px solid #2196F3'
      }}>
        <h3 style={{ color: '#1565C0', marginBottom: '15px' }}>📊 Resumen de Tramos</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              📏 Total Tramos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              {data.tramos.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🎯 Prof. Máxima
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {data.tramos.reduce((max, tramo) => Math.max(max, tramo.profundidadObjetivo || 0), 0)} m
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🔧 Tamaños Trepano
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {new Set(data.tramos.map(tramo => tramo.tamanioTrepan)).size}
            </div>
          </div>
        </div>

        {/* Tramos por Fase */}
        {data.tramos.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#1565C0', marginBottom: '10px' }}>Tramos por Fase:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
              {data.tramos.map((tramo, index) => (
                <div key={tramo.id || index} style={{
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #ddd'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1565C0' }}>
                    📏 {getFaseDisplayName(tramo.fase)}
                  </div>
                  <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                    <strong>Trepano:</strong> {tramo.tamanioTrepan}
                  </div>
                  <div style={{ fontSize: '18px', color: '#2196F3', fontWeight: 'bold' }}>
                    {tramo.profundidadObjetivo} m
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.tramos.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay tramos registrados. Usa la carga rápida para empezar con tramos estándar.
          </div>
        )}
      </div>
    </>
  );
};